import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import PageLayout from "examples/LayoutContainers/PageLayout";
import DefaultNavbar from "examples/Navbars/DefaultNavbar";
import Footer from "layouts/authentication/components/Footer";
import SkipToContent from "components/SkipToContent";
import usePageTitle from "utils/usePageTitle";
import pageRoutes from "page.routes";

import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import MenuItem from "@mui/material/MenuItem";
import Modal from "@mui/material/Modal";
import IconButton from "@mui/material/IconButton";
import useAPI from "utils/useApi";
import { primaryBtnSx } from "../formular-sesizare/colors";

const PALETTE = {
  navy: "#061D36",
  title: "#0f2c4a",
  body: "#5a6b85",
  muted: "#8b9cb0",
  blue: "#1565C0",
  yellow: "#FFC107",
  surface: "#f8f9fb",
  border: "rgba(6, 29, 54, 0.08)",
};

const CATEGORIES = [
  { key: "all", label: "Toate" },
  { key: "video", label: "Video" },
  { key: "podcast", label: "Podcast" },
  { key: "platform", label: "Platforme" },
  { key: "campaign", label: "Campanii" },
];

const SORT_OPTIONS = [
  { key: "recent", label: "Cele mai noi" },
  { key: "oldest", label: "Cele mai vechi" },
];

const panelSx = {
  bgcolor: "#fff",
  borderRadius: "12px",
  border: `1px solid ${PALETTE.border}`,
  boxShadow: "0 4px 20px rgba(6, 29, 54, 0.06)",
};

const NATIVE_VIDEO_RE = /\.(mp4|webm|ogg)(\?.*)?$/i;

function isNativeVideoFile(url = "") {
  return NATIVE_VIDEO_RE.test(url);
}

function getPosterUrl(item) {
  return item?.image || item?.poster || item?.thumbnail || item?.thumbnailUrl || null;
}

function getYouTubeVideoId(url = "") {
  try {
    const u = new URL(url, window.location.origin);
    if (u.hostname.includes("youtu.be")) return u.pathname.slice(1).split("/")[0] || null;
    if (u.hostname.includes("youtube.com") || u.hostname.includes("youtube-nocookie.com")) {
      if (u.pathname.startsWith("/embed/")) return u.pathname.split("/")[2] || null;
      return u.searchParams.get("v");
    }
  } catch {
    /* ignore */
  }
  const m = url.match(/(?:embed\/|youtu\.be\/|v=)([\w-]{11})/);
  return m ? m[1] : null;
}

function getVimeoVideoId(url = "") {
  try {
    const u = new URL(url, window.location.origin);
    const match = u.pathname.match(/\/(?:video\/)?(\d+)/);
    return match ? match[1] : null;
  } catch {
    const m = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
    return m ? m[1] : null;
  }
}

function captureNativeVideoFrame(src) {
  return new Promise((resolve) => {
    const video = document.createElement("video");
    video.muted = true;
    video.playsInline = true;
    video.preload = "auto";
    video.crossOrigin = "anonymous";

    let settled = false;
    const finish = (result) => {
      if (settled) return;
      settled = true;
      clearTimeout(timeout);
      video.removeAttribute("src");
      video.load();
      resolve(result);
    };

    const timeout = setTimeout(() => finish(null), 12000);

    video.addEventListener("error", () => finish(null));
    video.addEventListener("loadeddata", () => {
      const t =
        Number.isFinite(video.duration) && video.duration > 0
          ? Math.min(1, video.duration * 0.05)
          : 0.1;
      try {
        video.currentTime = t;
      } catch {
        finish(null);
      }
    });
    video.addEventListener("seeked", () => {
      try {
        const { videoWidth: w, videoHeight: h } = video;
        if (!w || !h) {
          finish(null);
          return;
        }
        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        canvas.getContext("2d").drawImage(video, 0, 0, w, h);
        finish(canvas.toDataURL("image/jpeg", 0.82));
      } catch {
        finish(null);
      }
    });

    video.src = src;
  });
}

function hasVideo(item) {
  return (item?.type || "").toLowerCase() === "video" || Boolean(item?.videoUrl);
}

function hasLightboxMedia(item) {
  const type = (item?.type || "").toLowerCase();
  return type === "video" || Boolean(item?.videoUrl) || (type === "image" && Boolean(item?.image));
}

function isCampaignLabel(label) {
  return (
    label.includes("campan") ||
    label.includes("proiect") ||
    label.includes("igpr") ||
    label.includes("jandarmerie")
  );
}

/** Tag explicit din CMS (ex. „VIDEO”, „Material video”). */
function hasVideoTag(label) {
  const l = (label || "").toLowerCase().trim();
  return l === "video" || /\bvideo\b/.test(l);
}

/** Categorie sidebar — type din API; videoclipuri fără tag VIDEO merg la Campanii. */
function getResourceCategory(item) {
  const type = (item?.type || "").toLowerCase();
  const label = (item.label || "").toLowerCase();

  if (label.includes("podcast")) return "podcast";
  if (label.includes("platform") || label.includes("platformă") || label.includes("platforma")) return "platform";
  if (isCampaignLabel(label)) return "campaign";

  if (hasVideoTag(label)) return "video";

  if (type === "image") return "campaign";

  // type video (ex. campanii IGPR/Jandarmerie) → Campanii, nu Video
  if (type === "video") return "campaign";

  return "platform";
}

function getCategoryLabel(item) {
  if (item.label) return item.label.toUpperCase();
  const type = (item?.type || "").toLowerCase();
  if (type === "video") return "VIDEO";
  if (type === "image") return "IMAGINE";
  const cat = getResourceCategory(item);
  const found = CATEGORIES.find((c) => c.key === cat);
  return found ? found.label.toUpperCase() : "RESURSĂ";
}

function getActionLabel(item) {
  if (item.linkLabel) return item.linkLabel.replace(/\s*→?\s*$/, "");
  if (hasLightboxMedia(item) && !item.link) return "Vizualizează";
  if (hasVideo(item)) return "Vizualizează";
  switch (getResourceCategory(item)) {
    case "podcast":
      return "Ascultă podcastul";
    case "platform":
      return "Vizitează platforma";
    default:
      return "Accesează resursa";
  }
}

function getEmbedVideoSrc(url) {
  const ytId = getYouTubeVideoId(url);
  if (ytId) return `https://www.youtube.com/embed/${ytId}?autoplay=1&rel=0`;
  const vimeoId = getVimeoVideoId(url);
  if (vimeoId) return `https://player.vimeo.com/video/${vimeoId}?autoplay=1`;
  return url;
}

function ResourceThumbnail({ item, onClick, clickable }) {
  const videoUrl = item?.videoUrl ?? "";
  const cmsPoster = getPosterUrl(item);
  const [poster, setPoster] = useState(cmsPoster);
  const [failed, setFailed] = useState(false);
  const isVideo = hasVideo(item);

  useEffect(() => {
    if (cmsPoster) {
      setPoster(cmsPoster);
      setFailed(false);
      return undefined;
    }

    if (!isVideo || !videoUrl) return undefined;

    let cancelled = false;

    (async () => {
      const ytId = getYouTubeVideoId(videoUrl);
      if (ytId) {
        if (!cancelled) setPoster(`https://img.youtube.com/vi/${ytId}/hqdefault.jpg`);
        return;
      }

      const vimeoId = getVimeoVideoId(videoUrl);
      if (vimeoId) {
        try {
          const res = await fetch(
            `https://vimeo.com/api/oembed.json?url=https://vimeo.com/${vimeoId}`
          );
          if (res.ok) {
            const data = await res.json();
            if (!cancelled && data?.thumbnail_url) {
              setPoster(data.thumbnail_url);
              return;
            }
          }
        } catch {
          /* fallback */
        }
      }

      if (isNativeVideoFile(videoUrl)) {
        const frame = await captureNativeVideoFrame(videoUrl);
        if (!cancelled && frame) setPoster(frame);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [videoUrl, cmsPoster, isVideo]);

  const showPoster = Boolean(poster) && !failed;
  const cat = getResourceCategory(item);
  const fallbackIcon =
    cat === "podcast" ? "podcasts" : cat === "platform" ? "language" : isVideo ? "play_circle" : "campaign";

  return (
    <SoftBox
      onClick={clickable ? onClick : undefined}
      sx={{
        width: "100%",
        height: "100%",
        position: "relative",
        cursor: clickable ? "pointer" : "default",
        bgcolor: "#0c1f32",
        overflow: "hidden",
      }}
    >
      {showPoster ? (
        <SoftBox
          component="img"
          src={poster}
          alt=""
          onError={() => setFailed(true)}
          sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
        />
      ) : item.type === "image" && item.image ? (
        <SoftBox
          component="img"
          src={item.image}
          alt=""
          sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
        />
      ) : (
        <SoftBox
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: `linear-gradient(135deg, ${PALETTE.navy} 0%, ${PALETTE.blue} 100%)`,
          }}
        >
          <Icon sx={{ color: "rgba(255,255,255,0.9)", fontSize: "2.5rem !important" }}>{fallbackIcon}</Icon>
        </SoftBox>
      )}

      {isVideo && (
        <SoftBox
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: showPoster || (item.type === "image" && item.image) ? "rgba(0,0,0,0.28)" : "transparent",
            pointerEvents: "none",
          }}
        >
          <SoftBox
            sx={{
              width: 36,
              height: 36,
              borderRadius: "50%",
              bgcolor: "rgba(255,255,255,0.94)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 2px 10px rgba(0,0,0,0.45)",
            }}
          >
            <Icon sx={{ fontSize: "1.4rem !important", color: PALETTE.navy, ml: "2px" }}>play_arrow</Icon>
          </SoftBox>
        </SoftBox>
      )}
    </SoftBox>
  );
}

function MediaLightbox({ item, open, onClose }) {
  if (!item) return null;

  const videoUrl = item.videoUrl ?? "";
  const isNative = isNativeVideoFile(videoUrl);
  const hasTextPanel = Boolean(item.title || item.text);
  const isVideoItem = hasVideo(item) && Boolean(videoUrl);

  const videoFrameSx = hasTextPanel
    ? { aspectRatio: "16 / 9", maxHeight: { xs: "50vh", sm: "54vh", md: "58vh" }, width: "100%" }
    : { aspectRatio: "16 / 9", maxHeight: { xs: "70vh", md: "80vh" }, width: "100%" };

  return (
    <Modal
      open={open}
      onClose={onClose}
      sx={{
        display: "flex",
        alignItems: hasTextPanel ? "flex-start" : "center",
        justifyContent: "center",
        p: { xs: 1.5, sm: 2 },
        overflow: "auto",
      }}
    >
      <SoftBox
        sx={{
          position: "relative",
          maxWidth: "min(960px, 96vw)",
          width: "100%",
          outline: "none",
          my: hasTextPanel ? { xs: 1, sm: 2 } : "auto",
          ...(!hasTextPanel && { maxHeight: { xs: "92vh", sm: "90vh" } }),
        }}
      >
        <SoftBox
          sx={{
            bgcolor: "#fff",
            borderRadius: "12px",
            overflow: "hidden",
            boxShadow: "0 24px 64px rgba(0,0,0,0.45)",
          }}
        >
          <SoftBox sx={{ position: "relative", flexShrink: 0, bgcolor: "#000" }}>
            <IconButton
              onClick={onClose}
              aria-label="Închide"
              sx={{
                position: "absolute",
                top: 8,
                right: 8,
                zIndex: 2,
                color: "#fff",
                bgcolor: "rgba(0,0,0,0.45)",
                "&:hover": { bgcolor: "rgba(0,0,0,0.65)" },
              }}
            >
              <Icon>close</Icon>
            </IconButton>

            {isVideoItem ? (
              isNative ? (
                <SoftBox sx={videoFrameSx}>
                  <video
                    src={videoUrl}
                    controls
                    autoPlay
                    playsInline
                    style={{ width: "100%", height: "100%", display: "block", objectFit: "contain" }}
                  />
                </SoftBox>
              ) : (
                <SoftBox sx={videoFrameSx}>
                  <iframe
                    src={getEmbedVideoSrc(videoUrl)}
                    title={item.title || "Video"}
                    allow="autoplay; fullscreen; picture-in-picture"
                    allowFullScreen
                    style={{ width: "100%", height: "100%", border: "none", display: "block" }}
                  />
                </SoftBox>
              )
            ) : item.type === "image" && item.image ? (
              <SoftBox
                component="img"
                src={item.image}
                alt={item.title || ""}
                sx={{
                  width: "100%",
                  maxHeight: hasTextPanel
                    ? { xs: "min(50vh, 360px)", sm: "min(54vh, 420px)" }
                    : { xs: "75vh", sm: "85vh" },
                  objectFit: "contain",
                  display: "block",
                }}
              />
            ) : null}
          </SoftBox>

          {hasTextPanel && (
            <SoftBox
              sx={{
                p: { xs: 2, sm: 2.5, md: 3 },
                flexShrink: 0,
                borderTop: `1px solid ${PALETTE.border}`,
                bgcolor: "#fff",
              }}
            >
              <SoftTypography
                variant="caption"
                sx={{
                  color: PALETTE.blue,
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: "1px",
                  fontSize: "0.65rem",
                  display: "block",
                  mb: 0.75,
                }}
              >
                {getCategoryLabel(item)}
              </SoftTypography>

              {item.title && (
                <SoftTypography
                  variant="h6"
                  component="h2"
                  sx={{
                    color: PALETTE.title,
                    fontWeight: 700,
                    fontSize: { xs: "1rem", sm: "1.1rem" },
                    lineHeight: 1.35,
                    mb: item.text ? 1.25 : 0,
                  }}
                >
                  {item.title}
                </SoftTypography>
              )}

              {item.text && (
                <SoftTypography
                  variant="body2"
                  component="p"
                  sx={{
                    color: PALETTE.body,
                    lineHeight: 1.65,
                    fontSize: { xs: "0.88rem", sm: "0.92rem" },
                    whiteSpace: "pre-line",
                    m: 0,
                  }}
                >
                  {item.text}
                </SoftTypography>
              )}
            </SoftBox>
          )}
        </SoftBox>
      </SoftBox>
    </Modal>
  );
}

function SidebarPanel({ title, children }) {
  return (
    <SoftBox sx={{ ...panelSx, p: 2.25, mb: 2 }}>
      <SoftTypography
        variant="caption"
        fontWeight="bold"
        sx={{
          color: PALETTE.blue,
          textTransform: "uppercase",
          letterSpacing: "1.2px",
          fontSize: "0.68rem",
          display: "block",
          mb: 1.5,
        }}
      >
        {title}
      </SoftTypography>
      {children}
    </SoftBox>
  );
}

const API_PAGE_SIZE = 12;

function parseResourceResults(data) {
  const list = Array.isArray(data) ? data : (data?.results ?? []);
  return list.filter(Boolean);
}

function mergeResourceItems(prev, next) {
  if (!prev.length) return next;
  const seen = new Set(prev.map((item) => item.id));
  const merged = [...prev];
  next.forEach((item) => {
    if (item.id == null || !seen.has(item.id)) {
      merged.push(item);
      if (item.id != null) seen.add(item.id);
    }
  });
  return merged;
}

function Multimedia() {
  usePageTitle("Resurse multimedia");
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [category, setCategory] = useState("all");
  const [sortBy, setSortBy] = useState("recent");
  const [searchQuery, setSearchQuery] = useState("");
  const [lightboxItem, setLightboxItem] = useState(null);
  const navigate = useNavigate();
  const client = useAPI();
  const clientRef = useRef(client);
  const loadingRef = useRef(false);
  const hasMoreRef = useRef(true);
  const pageRef = useRef(1);
  clientRef.current = client;

  const openLightbox = useCallback((item) => {
    if (hasLightboxMedia(item)) setLightboxItem(item);
  }, []);

  const closeLightbox = useCallback(() => setLightboxItem(null), []);

  const fetchResources = useCallback(async (pageNumber, append = false) => {
    loadingRef.current = true;
    if (append) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }

    try {
      const data = await clientRef.current.getResourcesContent({
        page: pageNumber,
        page_size: API_PAGE_SIZE,
      });
      console.log(data);
      const list = parseResourceResults(data);
      setItems((prev) => (append ? mergeResourceItems(prev, list) : list));
      const hasNextPage = Boolean(data?.next);
      setHasMore(hasNextPage);
      hasMoreRef.current = hasNextPage;
    } catch (error) {
      console.error("Error fetching multimedia content:", error);
      if (!append) setItems([]);
      setHasMore(false);
      hasMoreRef.current = false;
    } finally {
      loadingRef.current = false;
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  useEffect(() => {
    setItems([]);
    setPage(1);
    pageRef.current = 1;
    setHasMore(true);
    hasMoreRef.current = true;
    loadingRef.current = false;
    fetchResources(1, false);
  }, [fetchResources]);

  useEffect(() => {
    if (page === 1) return;
    if (!hasMoreRef.current) return;
    fetchResources(page, true);
  }, [page, fetchResources]);

  const requestNextPage = useCallback(() => {
    if (loadingRef.current || !hasMoreRef.current) return;
    loadingRef.current = true;
    const nextPage = pageRef.current + 1;
    pageRef.current = nextPage;
    setPage(nextPage);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (loadingRef.current || !hasMoreRef.current) return;
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      const scrollHeight = document.documentElement.scrollHeight;
      const clientHeight = window.innerHeight;
      if (scrollHeight - scrollTop - clientHeight < 240) {
        requestNextPage();
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [requestNextPage]);

  useEffect(() => {
    if (loading || loadingMore || !hasMore) return;
    const scrollHeight = document.documentElement.scrollHeight;
    const clientHeight = window.innerHeight;
    if (scrollHeight <= clientHeight + 240) {
      requestNextPage();
    }
  }, [items, loading, loadingMore, hasMore, requestNextPage]);

  const categoryCounts = useMemo(() => {
    const counts = { all: items.length };
    items.forEach((item) => {
      const key = getResourceCategory(item);
      counts[key] = (counts[key] || 0) + 1;
    });
    return counts;
  }, [items]);

  const visibleCategories = useMemo(
    () => CATEGORIES.filter(({ key }) => key === "all" || (categoryCounts[key] ?? 0) > 0),
    [categoryCounts]
  );

  useEffect(() => {
    if (category !== "all" && (categoryCounts[category] ?? 0) === 0) {
      setCategory("all");
    }
  }, [category, categoryCounts]);

  const filteredItems = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    let list = items.filter((item) => {
      const cat = getResourceCategory(item);
      if (category !== "all" && cat !== category) return false;
      if (!q) return true;
      const haystack = [item.title, item.text, item.label, item.linkLabel]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });

    list = [...list];
    if (sortBy === "oldest") list.reverse();
    return list;
  }, [items, category, searchQuery, sortBy]);

  return (
    <PageLayout>
      <SkipToContent />
      <DefaultNavbar
        routes={pageRoutes}
        transparent
        action={{
          type: "external",
          route: "/formular-sesizare",
          label: "Trimite Sesizare",
          color: "info",
        }}
      />

      <MediaLightbox item={lightboxItem} open={Boolean(lightboxItem)} onClose={closeLightbox} />

      <SoftBox
        component="main"
        id="main-content"
        tabIndex={-1}
        sx={{
          outline: "none",
          bgcolor: PALETTE.surface,
          minHeight: "60vh",
          mt: { xs: "125px", md: "140px" },
        }}
        mb={5}
        pt={{ xs: 1.5, md: 3 }}
        pb={{ xs: 2, md: 4 }}
        px={{ xs: 2, sm: 3, lg: 4 }}
      >
        <SoftBox maxWidth="1200px" mx="auto">
          <Grid container spacing={{ xs: 2, md: 3 }}>
            <Grid item xs={12} md={3} sx={{ order: { xs: 2, md: 1 } }}>
              <SidebarPanel title="Categorii">
                <SoftBox component="ul" sx={{ listStyle: "none", m: 0, p: 0 }}>
                  {visibleCategories.map(({ key, label }) => {
                    const active = category === key;
                    const count = categoryCounts[key] ?? 0;
                    return (
                      <SoftBox
                        component="li"
                        key={key}
                        onClick={() => setCategory(key)}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          px: 1.5,
                          py: 1,
                          mb: 0.5,
                          borderRadius: "8px",
                          cursor: "pointer",
                          bgcolor: active ? "rgba(21, 101, 192, 0.1)" : "transparent",
                          transition: "background 0.15s ease",
                          "&:hover": { bgcolor: active ? "rgba(21, 101, 192, 0.1)" : "rgba(6, 29, 54, 0.04)" },
                        }}
                      >
                        <SoftTypography
                          variant="button"
                          sx={{
                            color: active ? PALETTE.title : PALETTE.body,
                            fontWeight: active ? 700 : 500,
                            fontSize: "0.82rem",
                            textTransform: "none",
                          }}
                        >
                          {label}
                        </SoftTypography>
                        <SoftTypography variant="caption" sx={{ color: PALETTE.muted, fontWeight: 600 }}>
                          {count}
                        </SoftTypography>
                      </SoftBox>
                    );
                  })}
                </SoftBox>
              </SidebarPanel>

              <SidebarPanel title="Sortare">
                <TextField
                  select
                  fullWidth
                  size="small"
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "8px",
                      fontSize: "0.82rem",
                      bgcolor: "#fff",
                    },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Icon sx={{ color: PALETTE.muted, fontSize: "1.05rem !important" }}>sort</Icon>
                      </InputAdornment>
                    ),
                  }}
                  SelectProps={{
                    IconComponent: (props) => (
                      <Icon {...props} sx={{ fontSize: "1.2rem !important", color: PALETTE.muted, ...props.sx }}>
                        expand_more
                      </Icon>
                    ),
                  }}
                >
                  {SORT_OPTIONS.map(({ key, label }) => (
                    <MenuItem key={key} value={key}>
                      {label}
                    </MenuItem>
                  ))}
                </TextField>
              </SidebarPanel>
            </Grid>

            <Grid item xs={12} md={9} sx={{ order: { xs: 1, md: 2 } }}>
              <SoftBox
                display="flex"
                flexDirection={{ xs: "column", lg: "row" }}
                alignItems={{ xs: "stretch", lg: "flex-start" }}
                justifyContent="space-between"
                gap={2}
                mb={3}
              >
                <SoftBox flex={1} minWidth={0}>
                  <SoftBox display="flex" alignItems="flex-start">
                    <SoftBox
                      sx={{
                        width: "4px",
                        minHeight: "2.4rem",
                        bgcolor: PALETTE.yellow,
                        borderRadius: "2px",
                        mr: 1.5,
                        mt: 0.5,
                        flexShrink: 0,
                      }}
                    />
                    <SoftTypography
                      variant="h3"
                      fontWeight="bold"
                      sx={{ color: PALETTE.title, fontSize: { xs: "1.65rem", md: "2rem" }, lineHeight: 1.2 }}
                    >
                      Resurse Multimedia
                    </SoftTypography>
                  </SoftBox>
                  <SoftTypography
                    variant="body2"
                    sx={{
                      color: PALETTE.body,
                      mt: 1.5,
                      maxWidth: "640px",
                      lineHeight: 1.65,
                      fontSize: { xs: "0.88rem", md: "0.95rem" },
                    }}
                  >
                    Materiale video, podcasturi, ghiduri și platforme care te informează, te ajută și contribuie
                    la combaterea antisemitismului, rasismului și discursului instigator la ură.
                  </SoftTypography>
                </SoftBox>

                <TextField
                  placeholder="Caută resurse..."
                  size="small"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  sx={{
                    width: { xs: "100%", lg: "280px" },
                    flexShrink: 0,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "10px",
                      bgcolor: "#fff",
                      fontSize: "0.85rem",
                      boxShadow: "0 2px 8px rgba(6, 29, 54, 0.06)",
                    },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Icon sx={{ color: PALETTE.muted, fontSize: "1.1rem !important" }}>search</Icon>
                      </InputAdornment>
                    ),
                  }}
                />
              </SoftBox>

              {loading ? (
                <SoftBox textAlign="center" py={8} sx={panelSx}>
                  <Icon sx={{ fontSize: "2.5rem !important", color: PALETTE.blue, mb: 1.5 }}>hourglass_empty</Icon>
                  <SoftTypography variant="body1" sx={{ color: PALETTE.body }}>
                    Se încarcă resursele...
                  </SoftTypography>
                </SoftBox>
              ) : filteredItems.length === 0 ? (
                <SoftBox textAlign="center" py={8} sx={panelSx}>
                  <Icon sx={{ fontSize: "2.5rem !important", color: PALETTE.muted, mb: 1.5 }}>videocam_off</Icon>
                  <SoftTypography variant="h6" sx={{ color: PALETTE.title }}>
                    {items.length === 0
                      ? "Nu există conținut multimedia disponibil momentan."
                      : "Nicio resursă nu corespunde filtrelor selectate."}
                  </SoftTypography>
                </SoftBox>
              ) : (
                <SoftBox display="flex" flexDirection="column" gap={2}>
                  {filteredItems.map((item, idx) => {
                    const canPreview = hasLightboxMedia(item);
                    const externalLink = item.link || null;
                    const actionLabel = getActionLabel(item);

                    return (
                      <SoftBox
                        key={item.id ?? idx}
                        sx={{
                          ...panelSx,
                          p: { xs: 1.5, sm: 2 },
                          display: "flex",
                          flexDirection: { xs: "column", sm: "row" },
                          alignItems: { xs: "stretch", sm: "center" },
                          gap: { xs: 1.5, sm: 2 },
                          transition: "box-shadow 0.2s ease",
                          "&:hover": { boxShadow: "0 8px 28px rgba(6, 29, 54, 0.1)" },
                        }}
                      >
                        <SoftBox
                          sx={{
                            width: { xs: "100%", sm: 140 },
                            height: { xs: 160, sm: 90 },
                            flexShrink: 0,
                            borderRadius: "10px",
                            overflow: "hidden",
                            border: `1px solid ${PALETTE.border}`,
                          }}
                        >
                          <ResourceThumbnail
                            item={item}
                            clickable={canPreview}
                            onClick={() => openLightbox(item)}
                          />
                        </SoftBox>

                        <SoftBox
                          flex={1}
                          minWidth={0}
                          onClick={canPreview ? () => openLightbox(item) : undefined}
                          sx={{ cursor: canPreview ? "pointer" : "default" }}
                        >
                          <SoftTypography
                            variant="caption"
                            sx={{
                              color: PALETTE.blue,
                              fontWeight: 700,
                              textTransform: "uppercase",
                              letterSpacing: "1px",
                              fontSize: "0.65rem",
                              display: "block",
                              mb: 0.5,
                            }}
                          >
                            {getCategoryLabel(item)}
                          </SoftTypography>
                          <SoftTypography
                            variant="h6"
                            sx={{
                              color: PALETTE.title,
                              fontWeight: 700,
                              fontSize: { xs: "0.95rem", sm: "1rem" },
                              lineHeight: 1.35,
                              mb: 0.75,
                            }}
                          >
                            {item.title}
                          </SoftTypography>
                          {item.text && (
                            <SoftTypography
                              variant="body2"
                              sx={{
                                color: PALETTE.body,
                                lineHeight: 1.55,
                                fontSize: "0.84rem",
                                display: "-webkit-box",
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: "vertical",
                                overflow: "hidden",
                              }}
                            >
                              {item.text}
                            </SoftTypography>
                          )}
                        </SoftBox>

                        {(externalLink || canPreview) && (
                          externalLink ? (
                            <SoftBox
                              component="a"
                              href={externalLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 0.5,
                                color: PALETTE.blue,
                                fontWeight: 600,
                                fontSize: "0.82rem",
                                textDecoration: "none",
                                whiteSpace: { xs: "normal", sm: "nowrap" },
                                flexShrink: 0,
                                alignSelf: { xs: "flex-start", sm: "center" },
                                py: 0.5,
                                "&:hover": { color: PALETTE.navy },
                              }}
                            >
                              {actionLabel}
                              <Icon sx={{ fontSize: "1rem !important" }}>arrow_forward</Icon>
                            </SoftBox>
                          ) : (
                            <SoftBox
                              component="button"
                              type="button"
                              onClick={() => openLightbox(item)}
                              sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 0.5,
                                color: PALETTE.blue,
                                fontWeight: 600,
                                fontSize: "0.82rem",
                                background: "none",
                                border: "none",
                                cursor: "pointer",
                                whiteSpace: { xs: "normal", sm: "nowrap" },
                                flexShrink: 0,
                                alignSelf: { xs: "flex-start", sm: "center" },
                                py: 0.5,
                                fontFamily: "inherit",
                                "&:hover": { color: PALETTE.navy },
                              }}
                            >
                              {actionLabel}
                              <Icon sx={{ fontSize: "1rem !important" }}>arrow_forward</Icon>
                            </SoftBox>
                          )
                        )}
                      </SoftBox>
                    );
                  })}

                  {loadingMore && (
                    <SoftBox textAlign="center" py={3}>
                      <Icon sx={{ fontSize: "1.75rem !important", color: PALETTE.blue, mb: 1 }}>hourglass_empty</Icon>
                      <SoftTypography variant="body2" sx={{ color: PALETTE.body }}>
                        Se încarcă mai multe resurse...
                      </SoftTypography>
                    </SoftBox>
                  )}
                </SoftBox>
              )}

              <SoftBox
                mt={4}
                sx={{
                  background: `linear-gradient(135deg, ${PALETTE.navy} 0%, ${PALETTE.title} 100%)`,
                  borderRadius: "12px",
                  p: { xs: 3, md: 4 },
                  textAlign: "center",
                  boxShadow: "0 8px 28px rgba(6, 29, 54, 0.22)",
                }}
              >
                <SoftTypography variant="h5" color="white" fontWeight="bold" mb={1.5}>
                  Vrei să raportezi un incident?
                </SoftTypography>
                <SoftTypography variant="body2" sx={{ color: "rgba(255,255,255,0.88)", mb: 2.5, maxWidth: 520, mx: "auto" }}>
                  Completează formularul de sesizare și trimite către autoritățile competente
                </SoftTypography>
                <SoftButton variant="contained" onClick={() => navigate("/formular-sesizare")} sx={primaryBtnSx}>
                  <Icon sx={{ mr: 0.75 }}>send</Icon>
                  Trimite sesizare
                </SoftButton>
              </SoftBox>
            </Grid>
          </Grid>
        </SoftBox>
      </SoftBox>

      <Footer />
    </PageLayout>
  );
}

export default Multimedia;
